/**
 * TypeScript mirror of the public chat ModelMessage transport shape.
 *
 * Last schema sync: pydantic-ai-slim 1.102.0 (`pydantic_ai.messages` / `ModelMessagesTypeAdapter`).
 * Keep this in step with `sweetspot/core/pyproject.toml`; the `New*` types in this file are
 * frontend construction helpers, while the non-`New*` message/content types should match pydantic-ai's
 * serialized JSON shape except for backend-only fields stripped from public transport
 * (notably ModelRequest.instructions).
 */
// region: Format Types
import type { ThreadMessageLike } from '@assistant-ui/react'
import * as mime from 'mime-types'

export type DocumentFormat =
  | 'csv'
  | 'doc'
  | 'docx'
  | 'html'
  | 'md'
  | 'pdf'
  | 'txt'
  | 'xls'
  | 'xlsx'

export type AudioFormat = 'wav' | 'mp3' | 'oga' | 'flac' | 'aiff' | 'aac'

export type ImageFormat = 'jpeg' | 'png' | 'gif' | 'webp'

export type VideoFormat =
  | 'mkv'
  | 'mov'
  | 'mp4'
  | 'webm'
  | 'flv'
  | 'mpeg'
  | 'mpg'
  | 'wmv'
  | 'three_gp'

// endregion: Format Types

// region: Media Types

export type AudioMediaType =
  | 'audio/wav'
  | 'audio/mpeg'
  | 'audio/ogg'
  | 'audio/flac'
  | 'audio/aiff'
  | 'audio/aac'

export type DocumentMediaType =
  | 'application/pdf'
  | 'text/plain'
  | 'text/csv'
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  | 'text/html'
  | 'text/markdown'
  | 'application/msword'
  | 'application/vnd.ms-excel'

export type ImageMediaType =
  | 'image/jpeg'
  | 'image/png'
  | 'image/gif'
  | 'image/webp'

export type VideoMediaType =
  | 'video/x-matroska'
  | 'video/quicktime'
  | 'video/mp4'
  | 'video/webm'
  | 'video/x-flv'
  | 'video/mpeg'
  | 'video/x-ms-wmv'
  | 'video/3gpp'

function stripQueryAndHash(value: string): string {
  const queryIndex = value.indexOf('?')
  const hashIndex = value.indexOf('#')

  let end = value.length
  if (queryIndex !== -1) end = Math.min(end, queryIndex)
  if (hashIndex !== -1) end = Math.min(end, hashIndex)

  return value.slice(0, end)
}

function getUrlPathForMediaLookup(url: string): string {
  try {
    return decodeURIComponent(new URL(url).pathname).toLowerCase()
  } catch {
    try {
      return decodeURIComponent(stripQueryAndHash(url)).toLowerCase()
    } catch {
      return stripQueryAndHash(url).toLowerCase()
    }
  }
}

export function audioMediaType(url: string): AudioMediaType {
  const mimeType = mime.lookup(getUrlPathForMediaLookup(url))

  switch (mimeType) {
    case 'audio/mpeg':
    case 'audio/wav':
    case 'audio/flac':
    case 'audio/ogg':
    case 'audio/aiff':
    case 'audio/aac':
      return mimeType
    default:
      throw new Error(`Unknown audio file extension: ${url}`)
  }
}

export function documentMediaType(url: string): string {
  const mimeType = mime.lookup(getUrlPathForMediaLookup(url))

  if (!mimeType) {
    throw new Error(`unknown mimetype: ${url}`)
  }

  return mimeType
}

function uploadedFileMediaType(fileId: string): string {
  return (
    mime.lookup(getUrlPathForMediaLookup(fileId)) || 'application/octet-stream'
  )
}

export function documentFormat(media_type: string): DocumentFormat {
  switch (media_type) {
    case 'application/pdf':
      return 'pdf'
    case 'text/plain':
      return 'txt'
    case 'text/csv':
      return 'csv'
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'docx'
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'xlsx'
    case 'text/html':
      return 'html'
    case 'text/markdown':
      return 'md'
    case 'application/msword':
      return 'doc'
    case 'application/vnd.ms-excel':
      return 'xls'
    default:
      throw new Error(`Unknown document media type: ${media_type}`)
  }
}

export function imageMediaType(url: string): ImageMediaType {
  const path = getUrlPathForMediaLookup(url)

  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) {
    return 'image/jpeg'
  } else if (path.endsWith('.png')) {
    return 'image/png'
  } else if (path.endsWith('.gif')) {
    return 'image/gif'
  } else if (path.endsWith('.webp')) {
    return 'image/webp'
  } else {
    throw new Error(`Unknown image file extension: ${url}`)
  }
}

export function imageFormat(media_type: string): ImageFormat {
  switch (media_type) {
    case 'image/jpeg':
      return 'jpeg'
    case 'image/png':
      return 'png'
    case 'image/gif':
      return 'gif'
    case 'image/webp':
      return 'webp'
    default:
      throw new Error(`Unknown image media type: ${media_type}`)
  }
}

export function audioFormat(media_type: string): AudioFormat {
  switch (media_type) {
    case 'audio/mpeg':
      return 'mp3'
    case 'audio/wav':
      return 'wav'
    case 'audio/flac':
      return 'flac'
    case 'audio/ogg':
      return 'oga'
    case 'audio/aiff':
      return 'aiff'
    case 'audio/aac':
      return 'aac'
    default:
      throw new Error(`Unknown audio media type: ${media_type}`)
  }
}

function isYoutubeUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname
    return (
      hostname === 'youtu.be' ||
      hostname === 'youtube.com' ||
      hostname === 'www.youtube.com'
    )
  } catch {
    return false
  }
}

export function videoMediaType(url: string): VideoMediaType {
  if (isYoutubeUrl(url)) {
    return 'video/mp4'
  }

  const path = getUrlPathForMediaLookup(url)

  if (path.endsWith('.mkv')) {
    return 'video/x-matroska'
  } else if (path.endsWith('.mov')) {
    return 'video/quicktime'
  } else if (path.endsWith('.mp4')) {
    return 'video/mp4'
  } else if (path.endsWith('.webm')) {
    return 'video/webm'
  } else if (path.endsWith('.flv')) {
    return 'video/x-flv'
  } else if (path.endsWith('.mpeg') || path.endsWith('.mpg')) {
    return 'video/mpeg'
  } else if (path.endsWith('.wmv')) {
    return 'video/x-ms-wmv'
  } else if (path.endsWith('.3gp')) {
    return 'video/3gpp'
  } else {
    throw new Error(`Unknown video file extension: ${url}`)
  }
}

export function videoFormat(media_type: string): VideoFormat {
  switch (media_type) {
    case 'video/x-matroska':
      return 'mkv'
    case 'video/quicktime':
      return 'mov'
    case 'video/mp4':
      return 'mp4'
    case 'video/webm':
      return 'webm'
    case 'video/x-flv':
      return 'flv'
    case 'video/mpeg':
      return 'mpeg'
    case 'video/x-ms-wmv':
      return 'wmv'
    case 'video/3gpp':
      return 'three_gp'
    default:
      throw new Error(`Unknown video media type: ${media_type}`)
  }
}

// endregion: Media Types

// region: Media Content Types
export type ForceDownloadMode = boolean | 'allow-local'
/**
 * Type for the force_download parameter on FileUrl subclasses.
 *
 * - `False`: The URL is sent directly to providers that support it. For providers that don't,
 *   the file is downloaded with SSRF protection (blocks private IPs and cloud metadata).
 * - `True`: The file is always downloaded with SSRF protection (blocks private IPs and cloud metadata).
 * - `'allow-local'`: The file is always downloaded, allowing private IPs but still blocking cloud metadata.
 */

/**
 * Abstract base class for any URL-based file.
 */
export interface FileUrl {
  /**
   * The URL of the file.
   */
  url: string

  /**
   * Controls whether the file is downloaded and how SSRF protection is applied:
   *
   * * If `False`, the URL is sent directly to providers that support it. For providers that don't,
   *   the file is downloaded with SSRF protection (blocks private IPs and cloud metadata).
   * * If `True`, the file is always downloaded with SSRF protection (blocks private IPs and cloud metadata).
   * * If `'allow-local'`, the file is always downloaded, allowing private IPs but still blocking cloud metadata.
   */
  force_download: ForceDownloadMode

  /**
   * Vendor-specific metadata for the file.
   *
   * Supported by:
   * - `GoogleModel`: `VideoUrl.vendor_metadata` is used as `video_metadata`: https://ai.google.dev/gemini-api/docs/video-understanding#customize-video-processing
   * - `OpenAIChatModel`, `OpenAIResponsesModel`: `ImageUrl.vendor_metadata['detail']` is used as `detail` setting for images
   * - `XaiModel`: `ImageUrl.vendor_metadata['detail']` is used as `detail` setting for images
   */
  vendor_metadata: Record<string, unknown> | null

  /**
   * Return the media type of the file, based on the URL or the provided `media_type`.
   */
  media_type: string

  /**
   * The identifier of the file, such as a unique ID.
   *
   * This identifier can be provided to the model in a message to allow it to refer to this file in a tool call argument,
   * and the tool can look up the file in question by iterating over the message history and finding the matching `FileUrl`.
   *
   * This identifier is only automatically passed to the model when the `FileUrl` is returned by a tool.
   * If you're passing the `FileUrl` as a user message, it's up to you to include a separate text part with the identifier,
   * e.g. "This is file <identifier>:" preceding the `FileUrl`.
   *
   * It's also included in inline-text delimiters for providers that require inlining text documents, so the model can
   * distinguish multiple files.
   */
  identifier: string
}

/**
 * A URL to a video.
 */
export interface VideoUrl extends FileUrl {
  /**
   * The URL of the video.
   */
  url: string
  /**
   * Type identifier, this is available on all parts as a discriminator.
   */
  kind: 'video-url'
}

/**
 * A URL to an audio file.
 */
export interface AudioUrl extends FileUrl {
  /**
   * The URL of the audio file.
   */
  url: string
  /**
   * Type identifier, this is available on all parts as a discriminator.
   */
  kind: 'audio-url'
  /**
   * The media type of the audio file, based on the url.
   */
  media_type: string
}

/**
 * A URL to an image.
 */
export interface ImageUrl extends FileUrl {
  /**
   * The URL of the image.
   */
  url: string
  /**
   * Type identifier, this is available on all parts as a discriminator.
   */
  kind: 'image-url'
  media_type: string
}

/**
 * The URL of the document.
 */
export interface DocumentUrl extends FileUrl {
  /**
   * The URL of the document.
   */
  url: string
  /**
   * Type identifier, this is available on all parts as a discriminator.
   */
  kind: 'document-url'
}

/**
 * A binary content, e.g. an audio or image file.
 */
export interface BinaryContent {
  /**
   * The binary file data.
   *
   * Serialized by pydantic-ai as a base64url-encoded string.
   */
  data: string
  /**
   * The media type of the binary data.
   */
  media_type: AudioMediaType | ImageMediaType | DocumentMediaType | string
  /**
   * Identifier for the binary content, such as a unique ID.
   *
   * This identifier can be provided to the model in a message to allow it to refer to this file in a tool call argument,
   * and the tool can look up the file in question by iterating over the message history and finding the matching `BinaryContent`.
   *
   * This identifier is only automatically passed to the model when the `BinaryContent` is returned by a tool.
   * If you're passing the `BinaryContent` as a user message, it's up to you to include a separate text part with the identifier,
   * e.g. "This is file <identifier>:" preceding the `BinaryContent`.
   *
   * It's also included in inline-text delimiters for providers that require inlining text documents, so the model can
   * distinguish multiple files.
   */
  identifier: string
  /**
   * Vendor-specific metadata for the file.
   *
   * Supported by:
   * - `GoogleModel`: `BinaryContent.vendor_metadata` is used as `video_metadata`: https://ai.google.dev/gemini-api/docs/video-understanding#customize-video-processing
   * - `OpenAIChatModel`, `OpenAIResponsesModel`: `BinaryContent.vendor_metadata['detail']` is used as `detail` setting for images
   * - `XaiModel`: `BinaryContent.vendor_metadata['detail']` is used as `detail` setting for images
   */
  vendor_metadata: Record<string, unknown> | null
  /**
   * Type identifier, this is available on all parts as a discriminator.
   */
  kind: 'binary'
}

/**
 * A cache point marker for prompt caching.
 *
 * Can be inserted into UserPromptPart.content to mark cache boundaries.
 * Models that don't support caching will filter these out.
 *
 * Supported by:
 *
 * - Anthropic
 * - Amazon Bedrock (Converse API)
 */
export interface CachePoint {
  /**
   * Type identifier, this is available on all parts as a discriminator.
   */
  kind: 'cache-point'
  /**
   * The cache time-to-live, either "5m" (5 minutes) or "1h" (1 hour).
   *
   * Supported by:
   *
   * * Anthropic — see https://docs.claude.com/en/docs/build-with-claude/prompt-caching#1-hour-cache-duration for more information.
   * * Amazon Bedrock (Converse API) — see https://docs.aws.amazon.com/bedrock/latest/userguide/prompt-caching.html for more information.
   */
  ttl: '5m' | '1h'
}

/**
 * String content that is tagged with additional metadata.
 *
 * This is useful for including metadata that can be accessed programmatically by the application, but is not sent to the LLM.
 */
export interface TextContent {
  /**
   * The content that is sent to the LLM.
   */
  content: string
  /**
   * Additional data that can be accessed programmatically by the application but is not sent to the LLM.
   */
  metadata: unknown
  /**
   * Type identifier, this is available on all parts as a discriminator.
   */
  kind: 'text-content'
}

export type UploadedFileProviderName =
  | 'anthropic'
  | 'openai'
  | 'google'
  | 'google-cloud'
  | 'google-gla'
  | 'google-vertex'
  | 'bedrock'
  | 'xai'
/**
 * Provider names supported by [`UploadedFile`][pydantic_ai.messages.UploadedFile].
 *
 * The `google-gla` and `google-vertex` names are retained by pydantic-ai for backward
 * compatibility with histories captured before the v2 provider rename; current code emits
 * `google` and `google-cloud` respectively.
 */

/**
 * A reference to a file uploaded to a provider's file storage by ID.
 *
 * This allows referencing files that have been uploaded via provider-specific file APIs
 * rather than providing the file content directly.
 *
 * Supported by:
 *
 * - [`AnthropicModel`][pydantic_ai.models.anthropic.AnthropicModel]
 * - [`OpenAIChatModel`][pydantic_ai.models.openai.OpenAIChatModel]
 * - [`OpenAIResponsesModel`][pydantic_ai.models.openai.OpenAIResponsesModel]
 * - [`BedrockConverseModel`][pydantic_ai.models.bedrock.BedrockConverseModel]
 * - [`GoogleModel`][pydantic_ai.models.google.GoogleModel] (Gemini API: [Files API](https://ai.google.dev/gemini-api/docs/files) URIs, Google Cloud: GCS `gs://` URIs)
 * - [`XaiModel`][pydantic_ai.models.xai.XaiModel]
 */
export interface UploadedFile {
  /**
   * The provider-specific file identifier.
   *
   * For most providers, this is the file ID returned by the provider's upload API.
   * For GoogleModel (Google Cloud), this must be a GCS URI (`gs://bucket/path`).
   * For GoogleModel (Gemini API), this must be a Google Files API URI (`https://generativelanguage.googleapis.com/...`).
   * For BedrockConverseModel, this must be an S3 URI (`s3://bucket/key`).
   */
  file_id: string
  /**
   * The provider this file belongs to.
   *
   * This is required because file IDs are not portable across providers, and using a file ID
   * with the wrong provider will always result in an error.
   *
   * Tip: Use `model.system` to get the provider name dynamically.
   */
  provider_name: UploadedFileProviderName
  /**
   * Vendor-specific metadata for the file.
   *
   * The expected shape of this dictionary depends on the provider:
   *
   * Supported by:
   * - `GoogleModel`: used as `video_metadata` for video files
   */
  vendor_metadata: Record<string, unknown> | null
  /**
   * Return the media type of the file, inferred from `file_id` if not explicitly provided.
   *
   * Note: Inference relies on the file extension in `file_id`.
   * For opaque file IDs (e.g., `'file-abc123'`), the media type will default to `'application/octet-stream'`.
   * Inference relies on Python's `mimetypes` module, whose results may vary across platforms.
   *
   * Required by some providers (e.g., Bedrock) for certain file types.
   */
  media_type: string
  /**
   * The identifier of the file, such as a unique ID.
   *
   * This identifier can be provided to the model in a message to allow it to refer to this file in a tool call argument,
   * and the tool can look up the file in question by iterating over the message history and finding the matching `UploadedFile`.
   *
   * This identifier is only automatically passed to the model when the `UploadedFile` is returned by a tool.
   * If you're passing the `UploadedFile` as a user message, it's up to you to include a separate text part with the identifier,
   * e.g. "This is file <identifier>:" preceding the `UploadedFile`.
   */
  identifier: string
  /**
   * Type identifier, this is available on all parts as a discriminator.
   */
  kind: 'uploaded-file'
}

// endregion: Media Content Types

// region: Prompt Types

/**
 * A system prompt, generally written by the application developer.
 *
 * This gives the model context and guidance on how to respond.
 */
export interface SystemPromptPart {
  /**
   * The content of the prompt.
   */
  content: string
  /**
   * The timestamp of the prompt.
   */
  timestamp: string
  /**
   * The ref of the dynamic system prompt function that generated this part.
   *
   * Only set if system prompt is dynamic, see [`system_prompt`][pydantic_ai.Agent.system_prompt] for more information.
   */
  dynamic_ref?: string | null
  /**
   * Part type identifier, this is available on all parts as a discriminator.
   */
  part_kind: 'system-prompt'
}

export type MultiModalContent =
  | ImageUrl
  | AudioUrl
  | DocumentUrl
  | VideoUrl
  | BinaryContent
  | UploadedFile

/**
 * Union of all multi-modal content types with a discriminator for Pydantic validation.
 */

export type UserContent = string | TextContent | MultiModalContent | CachePoint

/** Indicates that a tool call has been approved and that the tool function should be executed. */
export interface ToolApproved {
  /** Optional tool call arguments to use instead of the original arguments. */
  override_args?: Record<string, unknown> | null
  kind: 'tool-approved'
}

/** Indicates that a tool call has been denied and that a denial message should be returned to the model. */
export interface ToolDenied {
  /** The message to return to the model. */
  message?: string
  kind: 'tool-denied'
}

/**
 * A structured tool return that separates the tool result from additional content sent to the model.
 *
 * Can be parameterized with a type to enable return schema generation:
 * - `ToolReturn[User]` — generates a return schema for `User`
 * - `ToolReturn` (bare) — no return schema generated
 */
export interface ToolReturn<T = unknown> {
  /** The return value to be used in the tool response. */
  return_value: T
  /**
   * Content sent to the model as a separate `UserPromptPart`.
   *
   * Use this when you want content to appear outside the tool result message.
   * For multimodal content that should be sent natively in the tool result,
   * return it directly from the tool function or include it in `return_value`.
   */
  content?: string | UserContent[] | null
  /** Additional data accessible by the application but not sent to the LLM. */
  metadata?: unknown
  kind: 'tool-return'
}

/** Exception to raise to request a model retry. */
export interface ModelRetry {
  /** The message to return to the model. */
  message: string
  kind: 'model-retry'
}

type NewImageUrlContent = {
  kind: 'image-url'
  url: string
} & Partial<
  Pick<
    ImageUrl,
    'force_download' | 'vendor_metadata' | 'media_type' | 'identifier'
  >
>

type NewAudioUrlContent = {
  kind: 'audio-url'
  url: string
} & Partial<
  Pick<
    AudioUrl,
    'force_download' | 'vendor_metadata' | 'media_type' | 'identifier'
  >
>

type NewDocumentUrlContent = {
  kind: 'document-url'
  url: string
} & Partial<
  Pick<
    DocumentUrl,
    'force_download' | 'vendor_metadata' | 'media_type' | 'identifier'
  >
>

type NewVideoUrlContent = {
  kind: 'video-url'
  url: string
} & Partial<
  Pick<
    VideoUrl,
    'force_download' | 'vendor_metadata' | 'media_type' | 'identifier'
  >
>

type NewBinaryContent = {
  kind: 'binary'
  data: string | Uint8Array
  media_type: AudioMediaType | ImageMediaType | DocumentMediaType | string
} & Partial<Pick<BinaryContent, 'identifier' | 'vendor_metadata'>>

type NewUploadedFileContent = {
  kind: 'uploaded-file'
  file_id: string
  provider_name: UploadedFileProviderName
} & Partial<Pick<UploadedFile, 'vendor_metadata' | 'media_type' | 'identifier'>>

export type NewUserContent =
  | string
  | TextContent
  | CachePoint
  | NewUploadedFileContent
  | NewImageUrlContent
  | NewAudioUrlContent
  | NewDocumentUrlContent
  | NewVideoUrlContent
  | NewBinaryContent

/**
 * A user prompt, generally written by the end user.
 *
 * Content comes from the `user_prompt` parameter of Agent.run, Agent.run_sync, and Agent.run_stream.
 */
export interface UserPromptPart {
  /**
   * The content of the prompt.
   * Can be a string or an array of UserContent.
   */
  content: string | UserContent[]
  /**
   * The timestamp of the prompt.
   */
  timestamp: string
  /**
   * Part type identifier, this is available on all parts as a discriminator.
   */
  part_kind: 'user-prompt'
}

export interface NewUserPromptPart extends Omit<UserPromptPart, 'content'> {
  content: string | NewUserContent[]
}

/**
 * A plain text response from a model.
 */
export interface TextPart {
  /**
   * The text content of the response.
   */
  content: string

  /**
   * Provider-specific identifier for the part.
   */
  id?: string | null

  /**
   * The model provider that produced this part, when known.
   */
  provider_name?: string | null

  /**
   * Provider-specific metadata for this part.
   */
  provider_details?: Record<string, unknown> | null

  /**
   * Part type identifier, this is available on all parts as a discriminator.
   */
  part_kind: 'text'
}

/**
 * A thinking response from a model.
 */
export interface ThinkingPart {
  /**
   * The thinking content of the response.
   */
  content: string

  /**
   * The identifier of the thinking part.
   */
  id: string | null

  /**
   * The signature of the thinking.
   */
  signature: string | null

  /**
   * The model provider that produced this part, when known.
   */
  provider_name?: string | null

  /**
   * Provider-specific metadata for this part.
   */
  provider_details?: Record<string, unknown> | null

  /**
   * Part type identifier, this is available on all parts as a discriminator.
   */
  part_kind: 'thinking'
}

// region: Tool Types

export type ToolPartKind = 'tool-search'

/**
 * The outcome of the tool call.
 *
 * - `'success'`: The tool executed successfully.
 * - `'failed'`: The tool raised an error during execution.
 * - `'denied'`: The tool call was denied by the approval mechanism.
 * - `'interrupted'`: The call was cancelled (user stop / worker shutdown)
 *   before a result was produced.
 */
export type ToolReturnOutcome = 'success' | 'failed' | 'denied' | 'interrupted'
export type FinishReason =
  | 'stop'
  | 'length'
  | 'content_filter'
  | 'tool_call'
  | 'error'
export type ModelResponseState =
  | 'complete'
  | 'incomplete'
  | 'suspended'
  | 'interrupted'

// Legacy read-side only: `outcome` ('interrupted' = cancelled, 'failed' =
// error) is the discriminator; these classify pre-migration rows.
export const TOOL_RETURN_SENTINEL_KEY = '__sweetspot_tool_return_sentinel__'
export type ToolReturnSentinelValue = 'tool_error' | 'tool_cancelled'

export interface RequestUsage {
  input_tokens: number
  cache_write_tokens: number
  cache_read_tokens: number
  output_tokens: number
  input_audio_tokens: number
  cache_audio_read_tokens: number
  output_audio_tokens: number
  details: Record<string, number>
}

export interface ToolSearchArgs {
  /**
   * Normalized search inputs.
   *
   * * Anthropic BM25 / regex: single-item list with the query string.
   * * OpenAI server-executed `tool_search`: the list of tool paths the model picked.
   * * OpenAI client-execution / local `search_tools` fallback: single-item list with
   *   the keywords string.
   */
  queries: string[]
}

export interface ToolSearchMatch {
  /**
   * Name of the discovered tool, as the model will call it.
   */
  name: string
  /**
   * Human-readable description, if the tool provided one.
   */
  description: string | null
}

export interface ToolSearchReturnContent {
  /**
   * Matches ordered by relevance. An empty list means "search ran, nothing matched".
   */
  discovered_tools: ToolSearchMatch[]
  /**
   * Optional text shown to the model when no matches were found.
   *
   * Rendered as text on local fallback / Anthropic custom-callable empty-results path.
   * Stripped on OpenAI client-execution and Anthropic server-side replay (those carry
   * only structural fields).
   */
  message?: string
}

/**
 * A tool return message, this encodes the result of running a tool.
 */
export interface ToolReturnPart {
  /**
   * The name of the "tool" that was called.
   */
  tool_name: string
  /**
   * The return value.
   */
  content: unknown
  /**
   * The tool call identifier, this is used by some models including OpenAI.
   */
  tool_call_id: string
  /**
   * The timestamp, when the tool returned.
   */
  timestamp: string
  /**
   * Part type identifier, this is available on all parts as a discriminator.
   */
  part_kind: 'tool-return'
  /**
   * Additional data that can be accessed programmatically by the application but is not sent to the LLM.
   */
  metadata?: unknown
  /**
   * The outcome of the tool call.
   *
   * - `'success'`: The tool executed successfully.
   * - `'failed'`: The tool raised an error during execution.
   * - `'denied'`: The tool call was denied by the approval mechanism.
   */
  outcome?: ToolReturnOutcome
  /**
   * The kind of tool call/result. Pydantic AI uses this for provider-specific
   * tool categories such as tool search.
   */
  tool_kind?: ToolPartKind | null
}

/**
 * A tool call from a model.
 */
export interface BaseToolCallPart {
  /**
   * The name of the tool to call.
   */
  tool_name: string

  /**
   * The arguments to pass to the tool.
   *
   * This is stored either as a JSON string or a Python dictionary depending on how data was received.
   */
  args: string | Record<string, unknown> | null

  /**
   * The tool call identifier, this is used by some models including OpenAI.
   *
   * In case the tool call id is not provided by the model, PydanticAI will generate a random one.
   */
  tool_call_id: string

  /**
   * The kind of tool call/result. Pydantic AI uses this for provider-specific
   * tool categories such as tool search.
   */
  tool_kind?: ToolPartKind | null

  /**
   * Provider-specific identifier for the part.
   */
  id?: string | null

  /**
   * The model provider that produced this part, when known.
   */
  provider_name?: string | null

  /**
   * Provider-specific metadata for this part.
   */
  provider_details?: Record<string, unknown> | null
}

export interface ToolCallPart extends BaseToolCallPart {
  /**
   * Part type identifier, this is available on all parts as a discriminator.
   */
  part_kind: 'tool-call'
}

export interface ToolSearchCallPart extends Omit<
  ToolCallPart,
  'tool_name' | 'args' | 'tool_kind'
> {
  tool_name: 'search_tools'
  args: string | ToolSearchArgs | null
  tool_kind: 'tool-search'
}

export interface NativeToolCallPart extends BaseToolCallPart {
  /**
   * Part type identifier, this is available on all parts as a discriminator.
   */
  part_kind: 'builtin-tool-call'
}

export interface NativeToolSearchCallPart extends Omit<
  NativeToolCallPart,
  'tool_name' | 'args' | 'tool_kind'
> {
  tool_name: 'tool_search'
  args: string | ToolSearchArgs | null
  tool_kind: 'tool-search'
}

export interface NativeToolReturnPart extends Omit<
  ToolReturnPart,
  'part_kind'
> {
  /**
   * Part type identifier, this is available on all parts as a discriminator.
   */
  part_kind: 'builtin-tool-return'

  /**
   * The model provider that produced this part, when known.
   */
  provider_name?: string | null

  /**
   * Provider-specific metadata for this part.
   */
  provider_details?: Record<string, unknown> | null
}

export interface ToolSearchReturnPart extends Omit<
  ToolReturnPart,
  'tool_name' | 'content' | 'tool_kind' | 'part_kind'
> {
  tool_name: 'search_tools'
  content: ToolSearchReturnContent
  tool_kind: 'tool-search'
  part_kind: 'tool-return'
}

export interface NativeToolSearchReturnPart extends Omit<
  NativeToolReturnPart,
  'tool_name' | 'content' | 'tool_kind' | 'part_kind'
> {
  tool_name: 'tool_search'
  content: ToolSearchReturnContent
  tool_kind: 'tool-search'
  part_kind: 'builtin-tool-return'
}

export interface CompactionPart {
  /**
   * The compaction summary text, if available.
   *
   * For Anthropic: a readable text summary of compacted messages.
   * For OpenAI: `None` (the compacted content is encrypted and stored in `provider_details`).
   */
  content?: string | null
  id?: string | null
  provider_name?: string | null
  provider_details?: Record<string, unknown> | null
  part_kind: 'compaction'
}

export interface FilePart {
  /**
   * The file content of the response.
   */
  content: BinaryContent
  id?: string | null
  provider_name?: string | null
  provider_details?: Record<string, unknown> | null
  part_kind: 'file'
}

export interface TextPartDelta {
  content_delta: string
  provider_name?: string | null
  provider_details?: Record<string, unknown> | null
  part_delta_kind: 'text'
}

export interface ThinkingPartDelta {
  content_delta?: string | null
  signature_delta?: string | null
  provider_name?: string | null
  provider_details?: Record<string, unknown> | null
  part_delta_kind: 'thinking'
}

export interface ToolCallPartDelta {
  tool_name_delta?: string | null
  args_delta?: string | Record<string, unknown> | null
  tool_call_id?: string | null
  provider_name?: string | null
  provider_details?: Record<string, unknown> | null
  part_delta_kind: 'tool_call'
}

export type ModelResponsePartDelta =
  | TextPartDelta
  | ThinkingPartDelta
  | ToolCallPartDelta

// endregion: Tool Types

/**
 * Details about a validation or runtime error, modeled after pydantic_core.ErrorDetails.
 */
export interface ErrorDetails {
  /**
   * The type of error that occurred, this is an identifier designed for programmatic use that will change rarely or never.
   * `type` is unique for each error message, and can hence be used as an identifier to build custom error messages.
   */
  type: string

  /**
   * Tuple of strings and ints identifying where in the schema the error occurred.
   * Python: tuple[int | str, ...]
   */
  loc: (number | string)[]

  /**
   * A human readable error message.
   */
  msg: string

  /**
   * The input data at this `loc` that caused the error.
   */
  input: unknown

  /**
   * Values which are required to render the error message, and could hence be useful in rendering custom error messages.
   * Also useful for passing custom error data forward.
   * Optional.
   */
  ctx?: Record<string, unknown>

  /**
   * The documentation URL giving information about the error. No URL is available if a PydanticCustomError is used.
   * Optional.
   */
  url?: string
}

/**
 * A message back to a model asking it to try again.
 *
 * This can be sent for a number of reasons:
 * - Pydantic validation of tool arguments failed (content is a list of error details)
 * - a tool raised a ModelRetry exception
 * - no tool was found for the tool name
 * - the model returned plain text when a structured response was expected
 * - Pydantic validation of a structured response failed (content is a list of error details)
 * - an output validator raised a ModelRetry exception
 */
export interface RetryPromptPart {
  /**
   * Details of why and how the model should retry.
   * If the retry was triggered by a ValidationError, this will be a list of error details.
   * Otherwise, it may be a string.
   * Python: content: list[pydantic_core.ErrorDetails] | str
   */
  content: string | ErrorDetails[]

  /**
   * The name of the tool that was called, if any.
   * Python: tool_name: str | None = None
   */
  tool_name?: string | null

  /**
   * The tool call identifier, this is used by some models including OpenAI.
   * In case the tool call id is not provided by the model, PydanticAI will generate a random one.
   * Python: tool_call_id: str = field(default_factory=_generate_tool_call_id)
   */
  tool_call_id: string

  /**
   * The timestamp, when the retry was triggered.
   * Python: timestamp: datetime = field(default_factory=_now_utc)
   */
  timestamp: string

  /**
   * Part type identifier, this is available on all parts as a discriminator.
   * Python: part_kind: Literal['retry-prompt'] = 'retry-prompt'
   */
  part_kind: 'retry-prompt'
}

// endregion: Prompt Types

// region: Message Types

export type ModelRequestPart =
  | SystemPromptPart
  | UserPromptPart
  | ToolSearchReturnPart
  | ToolReturnPart
  | RetryPromptPart

export type NewModelRequestPart =
  | SystemPromptPart
  | NewUserPromptPart
  | ToolSearchReturnPart
  | ToolReturnPart
  | RetryPromptPart

/**
 * A request generated by PydanticAI and sent to a model, e.g. a message from the PydanticAI app to the model
 */
export interface ModelRequest {
  /**
   * The parts of the user message.
   * Python: parts: list[ModelRequestPart]
   */
  parts: ModelRequestPart[]

  /**
   * Timestamp for the model request when pydantic-ai provides one.
   */
  timestamp?: string | null

  // ModelRequest.instructions is intentionally omitted from public chat
  // transports; the server keeps it only in native/DB persistence.

  /**
   * Message type identifier, this is available on all parts as a discriminator.
   * Python: kind: Literal['request'] = 'request'
   */
  kind: 'request'

  /**
   * Pydantic AI run identifier for this model message.
   */
  run_id?: string | null

  /**
   * Pydantic AI conversation identifier for model/provider continuity.
   */
  conversation_id?: string | null

  /**
   * Additional data accessible by the application but not sent to the LLM.
   * Python: metadata: dict[str, Any] | None = None
   */
  metadata?: Record<string, unknown> | null
}
export interface NewModelRequest extends Omit<ModelRequest, 'parts'> {
  parts: NewModelRequestPart[]
}

function defaultFileIdentifier(content: {
  identifier?: string | null
}): string {
  // Pydantic AI computes a stable identifier when this is omitted. For optimistic
  // frontend-only messages, keep the field present to match the serialized shape.
  return content.identifier ?? ''
}

function uint8ArrayToBase64Url(data: Uint8Array): string {
  const chunkSize = 0x8000
  let binary = ''
  for (let i = 0; i < data.byteLength; i += chunkSize) {
    binary += String.fromCharCode(...data.subarray(i, i + chunkSize))
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_')
}

export function formatModelRequest(request: NewModelRequest): ModelRequest {
  return {
    ...request,
    parts: request.parts.map((part): ModelRequestPart => {
      if (part.part_kind !== 'user-prompt') {
        return part
      }

      if (typeof part.content === 'string') {
        return {
          ...part,
          content: part.content,
        }
      } else {
        return {
          ...part,
          content: part.content.map((content): UserContent => {
            if (typeof content === 'string') {
              return content
            } else if (
              content.kind === 'text-content' ||
              content.kind === 'cache-point'
            ) {
              return content
            } else if (content.kind === 'uploaded-file') {
              return {
                file_id: content.file_id,
                provider_name: content.provider_name,
                vendor_metadata: content.vendor_metadata ?? null,
                kind: 'uploaded-file',
                media_type:
                  content.media_type ?? uploadedFileMediaType(content.file_id),
                identifier: defaultFileIdentifier(content),
              }
            } else if (content.kind === 'image-url') {
              return {
                url: content.url,
                force_download: content.force_download ?? false,
                vendor_metadata: content.vendor_metadata ?? null,
                kind: 'image-url',
                media_type: content.media_type ?? imageMediaType(content.url),
                identifier: defaultFileIdentifier(content),
              }
            } else if (content.kind === 'audio-url') {
              return {
                url: content.url,
                force_download: content.force_download ?? false,
                vendor_metadata: content.vendor_metadata ?? null,
                kind: 'audio-url',
                media_type: content.media_type ?? audioMediaType(content.url),
                identifier: defaultFileIdentifier(content),
              }
            } else if (content.kind === 'document-url') {
              return {
                url: content.url,
                force_download: content.force_download ?? false,
                vendor_metadata: content.vendor_metadata ?? null,
                kind: 'document-url',
                media_type:
                  content.media_type ?? documentMediaType(content.url),
                identifier: defaultFileIdentifier(content),
              }
            } else if (content.kind === 'video-url') {
              return {
                url: content.url,
                force_download: content.force_download ?? false,
                vendor_metadata: content.vendor_metadata ?? null,
                kind: 'video-url',
                media_type: content.media_type ?? videoMediaType(content.url),
                identifier: defaultFileIdentifier(content),
              }
            } else if (content.kind === 'binary') {
              return {
                data:
                  typeof content.data === 'string'
                    ? content.data
                    : uint8ArrayToBase64Url(content.data),
                media_type: content.media_type,
                vendor_metadata: content.vendor_metadata ?? null,
                kind: 'binary',
                identifier: defaultFileIdentifier(content),
              }
            } else {
              throw new Error(
                `Unknown content type: ${String((content as { kind?: string }).kind)}`,
              )
            }
          }),
        }
      }
    }),
  }
}

export type ModelResponsePart =
  | TextPart
  | ToolSearchCallPart
  | ToolCallPart
  | NativeToolSearchCallPart
  | NativeToolCallPart
  | NativeToolSearchReturnPart
  | NativeToolReturnPart
  | ThinkingPart
  | CompactionPart
  | FilePart

/**
 * A response from the model.
 */
export interface ModelResponse {
  /**
   * The parts of the model response.
   */
  parts: ModelResponsePart[]
  /**
   * Pydantic AI usage accounting for this model response.
   */
  usage?: RequestUsage
  /**
   * The name of the model that generated the response.
   */
  model_name: string | null | undefined
  /**
   * The timestamp of the response.
   */
  timestamp: string
  /**
   * Message type identifier, this is available on all parts as a discriminator.
   */
  kind: 'response'
  /**
   * The model provider that produced this response, when known.
   */
  provider_name?: string | null
  /**
   * Provider URL, when known.
   */
  provider_url?: string | null
  /**
   * Provider-specific metadata for this response.
   */
  provider_details?: Record<string, unknown> | null
  /**
   * Provider response identifier.
   */
  provider_response_id?: string | null
  /**
   * Why the model stopped generating.
   */
  finish_reason?: FinishReason | null
  /**
   * Pydantic AI run identifier for this model message.
   */
  run_id?: string | null
  /**
   * Pydantic AI conversation identifier for model/provider continuity.
   */
  conversation_id?: string | null
  /**
   * Additional data accessible by the application but not sent to the LLM.
   */
  metadata?: Record<string, unknown> | null
  /**
   * Whether this response is complete, still streaming/incomplete, or was interrupted.
   */
  state?: ModelResponseState
}

export type ModelMessage = ModelRequest | ModelResponse

// endregion: Message Types

// region: Tool Types

export type ToolID = string

export interface ToolInfo {
  id: ToolID
  name: string
  tool_call:
    | ToolSearchCallPart
    | ToolCallPart
    | NativeToolSearchCallPart
    | NativeToolCallPart
  tool_outcome:
    | ToolSearchReturnPart
    | ToolReturnPart
    | NativeToolSearchReturnPart
    | NativeToolReturnPart
    | RetryPromptPart
    | null
}

// endregion: Tool Types

// region: Assistant UI types

/**
 * Represents a JSON value that cannot be modified
 */
export interface ReadonlyJSONValue {
  readonly [key: string]:
    | ReadonlyJSONValue
    | null
    | string
    | number
    | boolean
    | readonly ReadonlyJSONValue[]
}

type ThreadMessageContent = ThreadMessageLike['content']
export type NonStringThreadMessageContent = Exclude<
  ThreadMessageContent,
  string
>[number]

// endregion: Assistant UI types

// region: Source Types

// TODO: migrate fully to this file
export type { Source } from '@/types/chat/Source'

// endregion: Source Types
